import React from 'react'
import { CarOutlined, FileTextOutlined, TagOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons'
import HeaderPadrao from './components/HeaderPadrao'
import FooterPadrao from './components/FooterPadrao'
import { groupByCliente, groupByCdPracaCliente, groupByImpressoraVeiculoCliente, sumSafe } from './utils/aggregators'

const SectionTitle = ({ children }) => (
  <h3 style={{ margin: '12px 0 6px', fontSize: '14px', borderBottom: '1px solid #e5e7eb' }}>{children}</h3>
)

const SimpleTable = ({ columns, data, tableId }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
    <thead>
      <tr>
        {columns.map((c) => (
          <th
            key={c.key}
            style={{
              textAlign: c.align || 'left',
              padding: '6px',
              background: '#eef2f7',
              color: '#0f172a',
              borderBottom: '1px solid #cbd5e1'
            }}
          >
            {c.title}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {data.map((row, idx) => (
        <tr key={row.key || idx} className={row._isTotal ? 'row-total' : undefined}>
          {columns.map((c) => {
            // Personalização por identificador: destacar troca de CD na tabela CD/Praça/Cliente
            const isCdTable = tableId === 'cd-praca-cliente'
            // Tentar identificar a coluna de CD de forma resiliente
            const cdCol = isCdTable
              ? (columns.find(col => (col.key && String(col.key).toLowerCase() === 'cd')
                || (col.dataIndex && String(col.dataIndex).toLowerCase() === 'cd')
                || (typeof col.title === 'string' && col.title.toLowerCase() === 'cd')) || { dataIndex: 'cd' })
              : null
            const cdField = cdCol?.dataIndex
            const prevCd = isCdTable ? (data[idx - 1] ? data[idx - 1][cdField] : undefined) : undefined
            const currentCd = isCdTable ? row[cdField] : undefined
            const isCdBoundary = isCdTable && !row._isTotal && currentCd != null && currentCd !== prevCd
            const cellStyle = {
              textAlign: c.align || 'left',
              padding: '4px 6px',
              borderBottom: '1px solid #e3e9e9'
            }
            if (isCdBoundary) {
              cellStyle.borderTop = '2px solid #1f2937'
            }
            return (
              <td key={c.key} style={cellStyle}>
                {typeof c.render === 'function' ? c.render(row[c.dataIndex], row) : row[c.dataIndex]}
              </td>
            )
          })}
        </tr>
      ))}
    </tbody>
  </table>
)

const RelatorioGerencial = ({ dadosClientes = [], dadosVeiculos = [], empresa = '', dateLabel = '', navbarInfo = {} }) => {
  // Identificadores de tabelas para personalização pontual
  const TABLE_ID = {
    clientesDia: 'clientes-dia',
    cdPracaCliente: 'cd-praca-cliente',
    impressorasVeiculoCliente: 'imp-veh-cli'
  }
  // Cálculos síncronos (sem hooks) para compatibilidade com renderização de string
  const calcularResumo = (lista) => {
    const clientesSet = new Set()
    const notasSet = new Set()
    let etiquetas = 0
    let volumes = 0
    let peso = 0
    let m3 = 0

    const arr = Array.isArray(lista) ? lista : []
    for (let idx = 0; idx < arr.length; idx++) {
      const i = arr[idx]
      const cliente = i?.Cliente || i?.cliente || i?.CLIENTE
      const nota = i?.nota || i?.NOTA || idx
      const etq = +((i?.etiquetas ?? i?.ETIQUETAS ?? 0))
      const vol = +((i?.VOLUMES ?? i?.volumes ?? 0))
      const ps = +((i?.PESO ?? i?.peso ?? 0))
      const mm = +((i?.M3 ?? i?.m3 ?? 0))
      if (cliente) clientesSet.add(cliente)
      if (nota) notasSet.add(nota)
      etiquetas += etq
      volumes += vol
      peso += ps
      m3 += mm
    }
    return { totalClientes: clientesSet.size, totalNotas: notasSet.size, etiquetas, volumes, peso, m3 }
  }

  const resumo = calcularResumo(dadosClientes)

  const clientesData = groupByCliente(dadosClientes)
  const clientesTotals = (() => {
    const base = { notas: 0, volumes: 0, etiquetasImp: 0, naoImpressas: 0, peso: 0, m3: 0 }
    for (let i = 0; i < clientesData.length; i++) {
      const r = clientesData[i]
      base.notas += r?.notas || 0
      base.volumes += r?.volumes || 0
      base.etiquetasImp += r?.etiquetasImp || 0
      base.naoImpressas += r?.naoImpressas || 0
      base.peso += r?.peso || 0
      base.m3 += r?.m3 || 0
    }
    return base
  })()
  const clientesDataWithTotal = [...clientesData, { key: 'clientes-total', cliente: 'TOTAL', ...clientesTotals }]
  const cdPracaClienteData = groupByCdPracaCliente(dadosClientes)
  const impressoraVeiculoClienteData = groupByImpressoraVeiculoCliente(dadosClientes)

  const calcularKpis = (veiculos, clientes) => {
    if (Array.isArray(veiculos) && veiculos.length > 0) {
      const veiculosUnicos = new Set(veiculos.map(v => v.CARRO))
      const totalVeiculos = veiculosUnicos.size
      const notasImp = veiculos.reduce((acc, v) => acc + (parseInt(v.TTL_NOTAS_IMP) || 0), 0)
      const notasSem = veiculos.reduce((acc, v) => acc + (parseInt(v.TTL_NOTAS_SEM) || 0), 0)
      const etiquetasImp = veiculos.reduce((acc, v) => acc + (parseInt(v.TTL_ETIQUETAS_IMP) || 0), 0)
      const etiquetasSem = veiculos.reduce((acc, v) => acc + (parseInt(v.TTL_ETIQUETAS_SEM) || 0), 0)
      const clientesCount = veiculos.reduce((acc, v) => acc + (parseInt(v.TTL_CLIENTES) || 0), 0)
      const coletasFinalizadas = veiculos.filter(v => String(v.status).toUpperCase() === 'FINALIZADA').length
      return { totalVeiculos, notasImp, notasSem, etiquetasImp, etiquetasSem, clientes: clientesCount, coletasFinalizadas }
    }

    const veiculosSet = new Set()
    const clientesSet = new Set()
    const notasImpSet = new Set()
    const notasSemSet = new Set()
    let etiquetasImp = 0
    let etiquetasSem = 0

      (Array.isArray(clientes) ? clientes : []).forEach((item, idx) => {
        if (item?.CARRO) veiculosSet.add(item.CARRO)
        if (item?.CLIENTE) clientesSet.add(item.CLIENTE)
        const notaVal = item?.Nota ?? item?.NOTA ?? idx
        const imp = item?.STATUS_ETIQUETA === 1
        if (imp) { notasImpSet.add(notaVal); etiquetasImp += 1 } else { notasSemSet.add(notaVal); etiquetasSem += 1 }
      })

    return { totalVeiculos: veiculosSet.size, notasImp: notasImpSet.size, notasSem: notasSemSet.size, etiquetasImp, etiquetasSem, clientes: clientesSet.size, coletasFinalizadas: 0 }
  }

  const kpis = calcularKpis(dadosVeiculos, dadosClientes)

  const clientesColumns = [
    { title: 'Cliente', key: 'cliente', dataIndex: 'cliente' },
    { title: 'Notas', key: 'notas', dataIndex: 'notas', align: 'right' },
    { title: 'Volumes', key: 'volumes', dataIndex: 'volumes', align: 'right' },
    { title: 'Etiquetas', key: 'etiquetasImp', dataIndex: 'etiquetasImp', align: 'right' },
    { title: 'Não Impressas', key: 'naoImpressas', dataIndex: 'naoImpressas', align: 'right' },
    { title: 'Peso', key: 'peso', dataIndex: 'peso', align: 'right', render: (v) => Number(v).toFixed(2) },
    { title: 'M3', key: 'm3', dataIndex: 'm3', align: 'right', render: (v) => Number(v).toFixed(3) },
  ]

  const cdColumns = [
    { title: 'CD', key: 'cd', dataIndex: 'cd' },
    { title: 'Praça', key: 'praca', dataIndex: 'praca' },
    { title: 'Notas', key: 'notas', dataIndex: 'notas', align: 'right' },
    { title: 'Volumes', key: 'volumes', dataIndex: 'volumes', align: 'right' },
    { title: 'Etiquetas', key: 'etiquetas', dataIndex: 'etiquetas', align: 'right' },
    { title: 'Peso', key: 'peso', dataIndex: 'peso', align: 'right', render: (v) => Number(v).toFixed(2) },
    { title: 'M3', key: 'm3', dataIndex: 'm3', align: 'right', render: (v) => Number(v).toFixed(3) },
  ]

  const impColumns = [
    { title: 'Impressora', key: 'impressora', dataIndex: 'impressora' },
    { title: 'Veículo', key: 'veiculo', dataIndex: 'veiculo' },
    { title: 'Cliente', key: 'cliente', dataIndex: 'cliente' },
    { title: 'Notas', key: 'notas', dataIndex: 'notas', align: 'right' },
    { title: 'Etiquetas', key: 'etiquetas', dataIndex: 'etiquetas', align: 'right' },
    { title: 'Volumes', key: 'volumes', dataIndex: 'volumes', align: 'right' },
    { title: 'Peso', key: 'peso', dataIndex: 'peso', align: 'right', render: (v) => Number(v).toFixed(2) },
    { title: 'M3', key: 'm3', dataIndex: 'm3', align: 'right', render: (v) => Number(v).toFixed(3) },
  ]

  const flattedCd = (() => {
    const rows = []
    cdPracaClienteData.forEach((cd) => {
      rows.push({ key: `cd-${cd.key}`, cd: cd.cd, praca: '', cliente: '', notas: cd.notas, volumes: cd.volumes, etiquetas: cd.etiquetas, peso: cd.peso, m3: cd.m3 })
      cd.children.forEach((pr) => {
        rows.push({ key: `pr-${pr.key}`, cd: '', praca: pr.praca, cliente: '', notas: pr.notas, volumes: pr.volumes, etiquetas: pr.etiquetas, peso: pr.peso, m3: pr.m3 })
        pr.children.forEach((cl) => {
          rows.push({ key: `cl-${cl.key}`, cd: '', praca: '', cliente: cl.cliente, notas: cl.notas, volumes: cl.volumes, etiquetas: cl.etiquetas, peso: cl.peso, m3: cl.m3 })
        })
      })
    })
    return rows
  })()

  // Totais para CD/Praça/Cliente (somando apenas nível CD para evitar dupla contagem)
  const cdTotals = (() => {
    const base = { notas: 0, volumes: 0, etiquetas: 0, peso: 0, m3: 0 }
    cdPracaClienteData.forEach((cd) => {
      base.notas += cd?.notas || 0
      base.volumes += cd?.volumes || 0
      base.etiquetas += cd?.etiquetas || 0
      base.peso += cd?.peso || 0
      base.m3 += cd?.m3 || 0
    })
    return base
  })()
  const flattedCdWithTotal = [...flattedCd, { key: 'cd-total', cd: 'TOTAL', praca: '', cliente: '', ...cdTotals, _isTotal: true }]

  const flattedImp = (() => {
    const rows = []
    impressoraVeiculoClienteData.forEach((p) => {
      rows.push({ key: `imp-${p.key}`, impressora: p.impressora, veiculo: '', cliente: '', notas: p.notas, etiquetas: p.etiquetas, volumes: p.volumes, peso: p.peso, m3: p.m3 })
      p.children.forEach((v) => {
        rows.push({ key: `veh-${v.key}`, impressora: '', veiculo: v.veiculo, cliente: '', notas: v.notas, etiquetas: v.etiquetas, volumes: v.volumes, peso: v.peso, m3: v.m3 })
        v.children.forEach((c) => {
          rows.push({ key: `cli-${c.key}`, impressora: '', veiculo: '', cliente: c.cliente, notas: c.notas, etiquetas: c.etiquetas, volumes: c.volumes, peso: c.peso, m3: c.m3 })
        })
      })
    })
    return rows
  })()

  const styles = (
    <style>{`
      @page { size: A4; margin: 12mm; }
      @media print {
        .no-print { display: none !important; }
      }
      .report-container { font-family: Arial, sans-serif; color: #111827; }
      .summary { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin: 12px 0; }
      .summary .card { border-radius: 8px; padding: 10px; color: #111827; }
      .card .label { font-size: 10px; opacity: 0.8; }
      .card .value { font-size: 18px; font-weight: 700; }
      .card.veiculos { background: #e3f2fd; border: 1px solid #90caf9; }
      .card.notas-imp { background: #e8f5e9; border: 1px solid #a5d6a7; }
      .card.notas-sem { background: #fff3e0; border: 1px solid #ffcc80; }
      .card.etq-imp { background: #e8f5e9; border: 1px solid #a5d6a7; }
      .card.etq-sem { background: #ffebee; border: 1px solid #ef9a9a; }
      .card.clientes { background: #ede7f6; border: 1px solid #b39ddb; }
      .card.coletas { background: #e0f2f1; border: 1px solid #80cbc4; }
      .section { margin-top: 10px; }
      .row-total { background: #f7f9fc; }
      .row-total td { font-weight: 600; }
    `}</style>
  )

  const generatedAt = new Date().toLocaleString()

  return (
    <div className="report-container">
      {styles}
      <HeaderPadrao
        title="Relatório Gerencial - Painel Operacional"
        empresa={empresa}
        dateLabel={dateLabel}
        navbarInfo={navbarInfo}
      />

      <div className="summary">
        <div className="card veiculos">
          <div className="label">Veículos</div>
          <div className="value">{kpis.totalVeiculos}</div>
        </div>
        <div className="card notas-imp">
          <div className="label">Notas (com impressão)</div>
          <div className="value">{kpis.notasImp}</div>
        </div>
        <div className="card notas-sem">
          <div className="label">Notas (sem impressão)</div>
          <div className="value">{kpis.notasSem}</div>
        </div>
        <div className="card etq-imp">
          <div className="label">Etiquetas (Impressas)</div>
          <div className="value">{kpis.etiquetasImp}</div>
        </div>
        <div className="card etq-sem">
          <div className="label">Etiquetas (Não Impressas)</div>
          <div className="value">{kpis.etiquetasSem}</div>
        </div>
        <div className="card clientes">
          <div className="label">Clientes</div>
          <div className="value">{kpis.clientes}</div>
        </div>
        <div className="card coletas">
          <div className="label">Coletas Finalizadas</div>
          <div className="value">{kpis.coletasFinalizadas}</div>
        </div>
      </div>

      <div className="section">
        <SectionTitle>Clientes do Dia</SectionTitle>
        <SimpleTable tableId={TABLE_ID.clientesDia} columns={clientesColumns} data={clientesDataWithTotal} />
      </div>

      <div className="section">
        <SectionTitle>CD/Praça/Cliente - Cargas</SectionTitle>
        <SimpleTable tableId={TABLE_ID.cdPracaCliente} columns={cdColumns} data={flattedCdWithTotal} />
      </div>

      <div className="section">
        <SectionTitle>Impressoras - Uso por Veículo e Cliente</SectionTitle>
        <SimpleTable tableId={TABLE_ID.impressorasVeiculoCliente} columns={impColumns} data={flattedImp} />
      </div>

      <FooterPadrao generatedAt={generatedAt} />
    </div>
  )
}

export default RelatorioGerencial